import React from 'react'
import styled from 'styled-components'
import { useIntl, FormattedMessage } from 'react-intl'
import { Input, Button, Typography } from 'antd'
import {
  EditFilled,
  CloseOutlined,
  NumberOutlined,
  HomeOutlined,
  MailOutlined
} from '@ant-design/icons'
import { Briefcase, Flag } from 'react-feather'

import { FlexRow, FlexColumn } from '../FlexboxReusables/FlexItems'
import { Company } from './types'

type CompanySegmentProps = Company & {
  setCompanyName: (companyName: string) => void
  setCompanyStreet: (street: string) => void
  setCompanyZipCode: (zipCode: string) => void
  setCompanyCity: (city: string) => void
  setCompanyCountry: (country: string) => void
  setEditBuyersDetails: (editBuyersDetails: boolean) => void
  editBuyersDetails: boolean
} 

const CompanySegment: React.FC<CompanySegmentProps> = (
  {
    name,
    setCompanyName,
    street,
    setCompanyStreet,
    zipCode,
    setCompanyZipCode,
    city,
    setCompanyCity,
    country,
    setCompanyCountry,
    setEditBuyersDetails,
    editBuyersDetails
  }: CompanySegmentProps) => {
  const intl = useIntl()

  return (
    <>
      <FlexRow width='100%' justifyContent='flex-end' alignItems='center'>
        <FlexRow justifyContent='flex-start'>
          <SubHeading level={4}>
            <FormattedMessage id='generic.buyersDetails' />
          </SubHeading>
        </FlexRow>
        <EditButton type='primary' onClick={() => setEditBuyersDetails(!editBuyersDetails)}>
          {!editBuyersDetails ? <EditIcon /> : <EndEditIcon />}
          <FormattedMessage id='generic.edit' />
        </EditButton>
      </FlexRow>
  
      <FlexRow width='100%' justifyContent='space-evenly' alignItems='center' margin='1em 0 2em 0'>
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.companyName' />
          </Description>
          {editBuyersDetails ? (
            <StyledInput
              prefix={<MailOutlined />}
              placeholder={intl.formatMessage({ id: 'generic.companyName' })}
              value={name}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          ) : (
            <Typography.Text>{name}</Typography.Text>
          )}
        </FlexColumn>

        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.street' />
          </Description>
          {editBuyersDetails ? (
            <StyledInput
              prefix={<Briefcase size={16} />}
              placeholder={intl.formatMessage({ id: 'generic.street' })}
              value={street}
              onChange={(e) => setCompanyStreet(e.target.value)}
            />
          ) : (
            <Typography.Text>{street}</Typography.Text>
          )}
        </FlexColumn>
      </FlexRow>
  
      <FlexRow width='100%' justifyContent='space-evenly' alignItems='center' margin='1em 0 2em 0'>
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.country' />
          </Description>
          {editBuyersDetails ? (
            <StyledInput
              prefix={<Flag size={16} />}
              placeholder={intl.formatMessage({ id: 'generic.country' })}
              value={country}
              onChange={(e) => setCompanyCountry(e.target.value)}
            />
          ) : (
            <Typography.Text>{country}</Typography.Text>
          )}
        </FlexColumn>
            
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.zipCode' />
          </Description>
          {editBuyersDetails ? (
            <StyledInput
              prefix={<NumberOutlined />}
              placeholder={intl.formatMessage({ id: 'generic.zipCode' })}
              value={zipCode}
              onChange={(e) => setCompanyZipCode(e.target.value)}
            />
          ) : (
            <Typography.Text>{zipCode}</Typography.Text>
          )}
        </FlexColumn>
      </FlexRow>
  
      <FlexRow width='100%' justifyContent='space-evenly' alignItems='center' margin='1em 0 2em 0'>
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.city' />
          </Description>
          {editBuyersDetails ? (
            <StyledInput
              prefix={<HomeOutlined />}
              placeholder={intl.formatMessage({ id: 'generic.city' })}
              value={city}
              onChange={(e) => setCompanyCity(e.target.value)}
              style={{ width: '25%' }}
            />
          ) : (
            <Typography.Text>{city}</Typography.Text>
          )}
        </FlexColumn>
      </FlexRow>
    </>
  )
}

export default CompanySegment

const SubHeading = styled(Typography.Title)`
  margin: 0 0 1.5em 0 !important;
`

const Description = styled(Typography.Text)`
  font-size: 0.8em;
  color: grey;
`

const EditButton = styled(Button)`
  margin: 0 0 1.5em 0 !important;
`

const EditIcon = styled(EditFilled)`
  margin-right: 1em;
`

const EndEditIcon = styled(CloseOutlined)`
  margin-right: 1em;
`

const StyledInput = styled(Input)`
  width: 50%;

  @media (max-width: 768px) {
    width: 100%;
  }
`